import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  output,
  signal,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  BdCompanyContextService,
  ShareableGrantFull,
} from '@bindoc-projects/bindoc-permissions-lib/data';
import {
  ShareableLabel,
  ShareableType,
} from '@bindoc-projects/resource-domain-lib';
import { BD_DANGER_MENU_ITEM_CSS } from '../../info-menu/danger-menu-item';
import { BdConfirmDeleteService } from '@bindoc-projects/ui-lib/dialog/ui';
import { ConfirmationService, MenuItem } from 'primeng/api';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { skip, take } from 'rxjs';

import { BdFolderTableComponent } from '../folder-table/folder-table.component';
import {
  FolderItem,
  ItemNavigateEvent,
  SHAREABLE_LIST_BROWSE,
  ShareableListPresentation,
} from '../shareables-overview.models';
import { BdSharedFolderNavigator } from '../shared-folder/shared-folder-navigator';
import { BdGrantActionsService } from './grant-actions.service';
import { grantToFolderItem } from './grant-to-folder-item';
import { BdGroupSharesService } from './group-shares.service';

/**
 * "Geteilt" inside a group's folder tree — the second tab of a group scope.
 *
 * Lists what reached the user *through this group*, as one flat list. The
 * question it answers is a property of the group, not of the open folder, so
 * the list does not change while navigating deeper: at every level it is the
 * group's grants.
 *
 * It is deliberately a *view*. Filing a share into a folder is a physical move
 * of the resource for everyone who can see it, so it belongs to the resource's
 * own list ("Ordner & Dateien" → "Verschieben") rather than to a list of who
 * was granted what. What stays here are the actions that act on the grant
 * itself — opening, labels, re-sharing, access, revoking.
 *
 * Note the deliberate overlap with the "Ordner & Dateien" tab: a share filed
 * into a group folder is a child of that folder and therefore listed in both.
 * The two tabs answer different questions — what is *in* this folder versus
 * what was *shared with this group* — so neither hides rows from the other.
 */
@Component({
  selector: 'bd-group-shared-tab',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <!--
      A group's grants arrive complete and are sorted here, so the Typ filter
      applies — the same rule the global "Geteilt" list follows. Off in a picker
      embedding, which brings its own criteria.
    -->
    <!--
      The path of an open shared folder is shown by the host's breadcrumb above
      the tabs (Firma › Gruppe › …), which owns the navigator.
    -->
    <bd-folder-table
      [items]="rows()"
      [loading]="folders.isOpen() ? folders.loading() : loading()"
      [notice]="folders.notice()"
      [presentation]="presentation()"
      [showType]="showType()"
      [typeFilter]="presentation().showActions"
      [sortMode]="'client'"
      [menuItemsProvider]="menuItemsProvider"
      [emptyMessage]="
        folders.isOpen()
          ? 'Dieser Ordner ist leer'
          : 'Mit dieser Gruppe wurde nichts geteilt'
      "
      (itemClick)="onItemClick($event)"
    ></bd-folder-table>
    <p-confirmdialog />
  `,
  styles: [
    `
      :host {
        display: flex;
        flex-direction: column;
        flex: 1 1 auto;
        min-height: 0;
      }
    `,
    BD_DANGER_MENU_ITEM_CSS,
  ],
  imports: [BdFolderTableComponent, ConfirmDialog],
  providers: [
    ConfirmationService,
    BdConfirmDeleteService,
    BdGrantActionsService,
  ],
})
export class BdGroupSharedTabComponent {
  public readonly groupId = input.required<string>();
  public readonly shareableType = input<ShareableType | undefined>(undefined);
  public readonly shareableTypes = input<readonly ShareableType[] | undefined>(
    undefined,
  );
  public readonly showType = input(false);
  public readonly presentation = input<ShareableListPresentation>(
    SHAREABLE_LIST_BROWSE,
  );

  public readonly itemNavigate = output<ItemNavigateEvent>();
  /** A grant was revoked — what the surrounding folder list shows is stale. */
  public readonly changed = output<void>();

  public readonly loading = signal(true);

  /**
   * Browsing into a shared folder without leaving the group (BDA-6454). The
   * open folder is the workspace location's (`?view=shared&folderId=…`); the
   * host tab's breadcrumb shows its path.
   */
  protected readonly folders = inject(BdSharedFolderNavigator);

  private readonly _grants = signal<readonly ShareableGrantFull[]>([]);

  private readonly _sharesService = inject(BdGroupSharesService);
  private readonly _grantActions = inject(BdGrantActionsService);
  private readonly _companyContext = inject(BdCompanyContextService);
  private readonly _destroyRef = inject(DestroyRef);

  /** Grants of this group, narrowed to the types the host lists. */
  private readonly _typedGrants = computed<readonly ShareableGrantFull[]>(
    () => {
      const types = this._effectiveTypes();
      const grants = this._grants();
      if (!types) {
        return grants;
      }
      return grants.filter((g) => types.includes(g.shareableReference.type));
    },
  );

  public readonly items = computed<FolderItem[]>(() =>
    this._typedGrants().map((grant) => grantToFolderItem(grant)),
  );

  /**
   * What the table shows: the group's shares, or the contents of the shared
   * folder that is open. Inside a folder the type filter narrows files only.
   */
  public readonly rows = computed<FolderItem[]>(() => {
    if (!this.folders.isOpen()) {
      return this.items();
    }
    const types = this._effectiveTypes();
    const items = this.folders.items();
    return types
      ? items.filter((i) => i.isFolder || types.includes(i.type))
      : [...items];
  });

  private readonly _grantsByItemId = computed(() => {
    const map = new Map<string, ShareableGrantFull>();
    this._grants().forEach((g) => {
      const id = g.shareableReference?.id;
      if (id && !map.has(id)) {
        map.set(id, g);
      }
    });
    return map;
  });

  public readonly menuItemsProvider = (item: FolderItem): MenuItem[] =>
    this.folders.isOpen()
      ? this.presentation().showActions
        ? this.folders.menuItems(item, (row) => this.onItemClick(row))
        : []
      : this._grantMenuItems(item);

  private readonly _grantMenuItems = (item: FolderItem): MenuItem[] =>
    this._grantActions.menuItems(item, {
      grant: this._grantsByItemId().get(item.id),
      showActions: this.presentation().showActions,
      onOpen: (row) => this.onItemClick(row),
      onLabelsChanged: (row, labels) => this._patchLabels(row, labels),
      onRevoked: () => this._refresh(),
    });

  constructor() {
    // Another group shares other things. Its location starts at the list of
    // shares already (see `reduceWorkspace`); the list is this tab's to read.
    effect(() => {
      this.groupId();
      untracked(() => this._reload());
    });

    // The grants endpoint is scoped by company; a context switch invalidates
    // everything this tab shows.
    this._companyContext.selectedCompanyId$
      .pipe(skip(1), takeUntilDestroyed(this._destroyRef))
      .subscribe(() => {
        this._sharesService.invalidate();
        this._reload();
      });
  }

  public onItemClick(item: FolderItem): void {
    // A folder opens here, in the group it was shared with, instead of in the
    // grantor's tree.
    if (item.isFolder) {
      this.folders.open(item);
      return;
    }
    this.itemNavigate.emit({ id: item.id, type: item.type, name: item.name });
  }

  private _sharedFolderIds(): ReadonlySet<string> {
    return new Set(
      this._grants()
        .filter((g) => g.shareableReference?.type === ShareableType.FOLDER)
        .map((g) => g.shareableReference.id),
    );
  }

  private _patchLabels(item: FolderItem, labels: ShareableLabel[]): void {
    this._grants.update((grants) =>
      grants.map((g) =>
        g.shareableReference?.id === item.id
          ? {
              ...g,
              shareableReference: { ...g.shareableReference, labels },
            }
          : g,
      ),
    );
  }

  /**
   * Re-reads after revoking, which removed a grant: the cached index would
   * otherwise answer with the state from before. The host is told as well —
   * losing a grant can change what the folder list may show.
   */
  private _refresh(): void {
    this._sharesService.invalidate();
    this._reload();
    this.changed.emit();
  }

  private _reload(): void {
    const groupId = this.groupId();
    if (!groupId) {
      return;
    }
    this.loading.set(true);
    this._sharesService
      .grantsOf(groupId)
      .pipe(take(1), takeUntilDestroyed(this._destroyRef))
      .subscribe((grants) => {
        this._grants.set(grants);
        this.loading.set(false);
        // A shared folder's path starts at the folder this group was given.
        this.folders.setSharedFolderIds(this._sharedFolderIds());
      });
  }

  /**
   * `shareableTypes` (multi-type) takes precedence over the single-type
   * `shareableType` input when both are set.
   */
  private _effectiveTypes(): ShareableType[] | undefined {
    const multi = this.shareableTypes();
    if (multi) {
      return [...multi];
    }
    const single = this.shareableType();
    return single ? [single] : undefined;
  }
}
